// packages/web-ui/src/components/kb-change-tree-shell/KbChangeTreeShell.tsx
//
// Story W2-S6 AC #5 — chat-inline shell that hosts the W4-S4
// `<KbChangeTree mode="inline">` component.
//
// Per the orchestrator brief, W4-S4 has already shipped the full per-entity
// tree (~1500 LOC) at `packages/web-ui/src/components/kb-change-tree/`,
// exposing `mode: 'inline' | 'full'`. This shell is the chat-side host
// chrome that wraps it: a CardShell envelope + primary accept-all /
// reject-all actions + a secondary "Open in Diff tab" link.
//
// 2026-05-18 rework: previously the shell pushed users to the Diff tab via
// a single prominent "OPEN DIFF DESTINATION" button. Users reported that
// accepting changes required a tab switch even though the inline tree
// already supports per-entity accept/reject. The shell now exposes
// changeset-wide Accept all / Reject all directly, and demotes the Diff
// hand-off to a small footer link.

import type { JSX } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import { CardShell } from '../inline-cards/CardShell.js';
import { KbChangeTree } from '../kb-change-tree/index.js';
import type { ChangesetSlice } from '../../state/changeset.js';
import type { KbChangeTreeShellEnvelopeData } from '../../state/inline-cards.js';

export interface KbChangeTreeShellProps extends KbChangeTreeShellEnvelopeData {
  /** Click handler for the secondary "Open in Diff tab" link. */
  onOpenDiff: (changesetId: string) => void;
  /**
   * When true, the inline tree is hidden and the shell renders only the
   * summary + actions (defensive fallback for environments where W4-S4
   * wiring is not yet complete in the surrounding RootStore composition).
   * The integration agent flips this off once root-store wiring lands.
   * Default false.
   */
  treeDisabled?: boolean;
  /**
   * W4 fix-pass HIGH-G — optional changeset store. When supplied, the
   * embedded `<KbChangeTree mode="inline">` reads from this store
   * instead of constructing its own module-level singleton. Pass the
   * root store from the chat panel so the inline tree shares state
   * with the Diff tab.
   *
   * The shell also subscribes to this store (when supplied) to drive the
   * Accept all / Reject all buttons and live entity counts. When omitted,
   * the action buttons are hidden — they would be no-ops without a store
   * to act on.
   */
  store?: StoreApi<ChangesetSlice>;
}

export function KbChangeTreeShell(
  props: KbChangeTreeShellProps,
): JSX.Element {
  // Subscribe to per-entity counts so the action row can disable itself
  // when there is nothing left to act on. When no store is supplied (the
  // defensive-fallback test path), we render the actions disabled.
  const entityStates = useStore(
    props.store ?? makeNoopStore(),
    (s: ChangesetSlice) => s.entityStatesByChangesetId[props.changesetId],
  );
  const acceptAll = useStore(
    props.store ?? makeNoopStore(),
    (s: ChangesetSlice) => s.acceptAll,
  );
  const rejectAll = useStore(
    props.store ?? makeNoopStore(),
    (s: ChangesetSlice) => s.rejectAll,
  );

  const counts = computeCounts(entityStates);
  const hasActionableEntities = counts.pending > 0;
  const actionsEnabled = props.store !== undefined && hasActionableEntities;

  const handleAcceptAll = (): void => {
    if (!actionsEnabled) return;
    void acceptAll(props.changesetId);
  };

  const handleRejectAll = (): void => {
    if (!actionsEnabled) return;
    void rejectAll(props.changesetId);
  };

  return (
    <CardShell
      role="region"
      ariaLabel="Pending changeset"
      cardKind="kb-change-tree-shell"
      testId="kb-change-tree-shell"
    >
      <div
        className="shell-header text-fg mb-2 flex items-baseline justify-between gap-2 font-medium"
        data-testid="kb-change-tree-shell-header"
      >
        <span>
          {props.entityCount} {props.entityCount === 1 ? 'entity' : 'entities'} proposed
        </span>
        <span
          className="text-taupe-700 text-[11px] font-normal"
          data-testid="kb-change-tree-shell-status"
        >
          {counts.accepted} accepted · {counts.rejected} rejected · {counts.pending} pending
        </span>
      </div>

      {!props.treeDisabled ? (
        <div
          className="kb-change-tree-shell-tree mb-3"
          data-testid="kb-change-tree-shell-tree"
        >
          <KbChangeTree
            changesetId={props.changesetId}
            mode="inline"
            {...(props.store ? { store: props.store } : {})}
          />
        </div>
      ) : null}

      <div
        className="kb-change-tree-shell-actions mb-2 flex gap-2"
        data-testid="kb-change-tree-shell-actions"
      >
        <button
          type="button"
          data-testid="kb-change-tree-shell-accept-all"
          aria-label={`Accept all entities in changeset ${props.changesetId}`}
          disabled={!actionsEnabled}
          onClick={handleAcceptAll}
          className="bg-taupe-900 disabled:bg-taupe-400 hover:bg-audi-red flex-1 rounded-none px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-white transition-colors disabled:cursor-not-allowed"
        >
          Accept all
        </button>
        <button
          type="button"
          data-testid="kb-change-tree-shell-reject-all"
          aria-label={`Reject all entities in changeset ${props.changesetId}`}
          disabled={!actionsEnabled}
          onClick={handleRejectAll}
          className="border-taupe-900 text-taupe-900 disabled:border-taupe-400 disabled:text-taupe-400 hover:bg-cream-100 flex-1 rounded-none border px-3 py-2 text-[11px] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed"
        >
          Reject all
        </button>
      </div>

      <button
        type="button"
        data-testid="kb-change-tree-shell-cta"
        aria-label={`Open Diff tab for changeset ${props.changesetId}`}
        onClick={() => props.onOpenDiff(props.changesetId)}
        className="text-taupe-700 hover:text-audi-red text-xs underline-offset-2 hover:underline"
      >
        Open in Diff tab for wider view →
      </button>

      <div
        className="hint text-taupe-700 mt-1 text-xs"
        data-testid="kb-change-tree-shell-hint"
      >
        Click a row to accept or reject a single entity. Use Accept all / Reject all
        to apply the decision to the whole changeset.
      </div>
    </CardShell>
  );
}

interface ShellCounts {
  accepted: number;
  rejected: number;
  pending: number;
}

function computeCounts(entityStates: unknown): ShellCounts {
  if (!Array.isArray(entityStates)) {
    return { accepted: 0, rejected: 0, pending: 0 };
  }
  let accepted = 0;
  let rejected = 0;
  let pending = 0;
  for (const s of entityStates) {
    const status = (s as { status?: string }).status;
    if (status === 'accepted' || status === 'applied') accepted += 1;
    else if (status === 'rejected') rejected += 1;
    else if (status === 'pending') pending += 1;
  }
  return { accepted, rejected, pending };
}

// Minimal noop-store used only when the parent did not pass a `store` prop.
// `useStore` requires a real store reference even on the no-op branch — we
// return one whose actions are no-ops and whose selectors return safe
// defaults. This branch is exercised by the legacy `treeDisabled` test
// path; production callers always supply the composed root store.
function makeNoopStore(): StoreApi<ChangesetSlice> {
  const state: Partial<ChangesetSlice> = {
    changesetsById: {},
    entityStatesByChangesetId: {},
    acceptAll: async () => undefined,
    rejectAll: async () => undefined,
  };
  return {
    getState: () => state as ChangesetSlice,
    setState: () => undefined,
    subscribe: () => () => undefined,
    getInitialState: () => state as ChangesetSlice,
  } as unknown as StoreApi<ChangesetSlice>;
}
